import { Link, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useEffect, useState } from "react";
import api from "../api/api";
import { getCached, setCached, isFresh } from "../utils/apiCache";
import "../styles/components/navbar.scss";
import luma3dLogo from "../assets/luma3d-logo.png";

// Fixed display order for the four permanent root categories - matches
// Home.js. Categories have no slug to hang a stable identifier off of, so
// the nav is built from real fetched category data (id, name) rather than
// a hardcoded list, using name only to order/display them.
const ROOT_ORDER = ["Products", "3D Printers", "Filaments", "Accessories"];
const CACHE_KEY = "categories";

function Navbar({ transparent = false }) {
  const { toggleCart, count } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [rootCategories, setRootCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    const toRoots = (data) =>
      data
        .filter((category) => !category.parent_id)
        .sort((a, b) => ROOT_ORDER.indexOf(a.name) - ROOT_ORDER.indexOf(b.name));

    // Shares a cache key with Home.js/Categories.js - whichever of these
    // loads first on a given visit warms it for the others.
    const cached = getCached(CACHE_KEY);
    if (cached) {
      setRootCategories(toRoots(cached.data));
    }

    if (!isFresh(cached)) {
      api
        .get("/categories")
        .then((res) => {
          if (!active) return;
          const data = Array.isArray(res.data?.data) ? res.data.data : [];
          setCached(CACHE_KEY, data);
          setRootCategories(toRoots(data));
        })
        .catch(() => {});
    }

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <nav className={`navbar ${transparent ? "navbar--overlay" : ""}`}>
      <div className="logo">
        <Link to="/">
          <img className="navbar-logo" src={luma3dLogo} alt="Luma3D" />
        </Link>
      </div>

      <button
        type="button"
        className="menu-icon"
        aria-expanded={menuOpen}
        aria-controls="primary-menu"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span className={`hamburger ${menuOpen ? "open" : ""}`} />
      </button>

      <ul id="primary-menu" className={`menu ${menuOpen ? "open" : ""}`}>
        {rootCategories.map((category) => (
          <li key={category.id}>
            <Link to={`/c/${category.id}`}>{category.name}</Link>
          </li>
        ))}
        <li>
          <button className="cart-btn" onClick={toggleCart}>
            Cart
            {count > 0 && <span className="cart-badge">{count}</span>}
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default Navbar;